import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  Switch,
  FormControlLabel,
  Collapse
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';
import { DeepLinkServices } from './DeepLinkServices';
import { QuickBookService } from '../QuickBook/Service';
import { quickBookModeLabel } from '../QuickBook/display';
import { usePermission } from '../../hooks/usePermission';
import { fromIstInput, toIstInput } from './shortLinkDisplay';
import { editDeepLinkUrl, loadDeepLinkEditor, sameDeepLinkDestination } from './deepLinkEditor';
import ShortLinkDialog from './ShortLinkDialog';

const deepLinkDefaults = {
      linkType: 'sub-category',
      categoryId: '',
      subCategoryId: '',
      masterServiceId: '',
      childServiceId: '',
      providerId: '',
      bookingId: '',
      quickBookMode: '',
      quickBookPath: [],
      addUtm: false,
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
      short_title: '', custom_alias: '', expires_at: '', is_active: true,
    };

const GenerateDeepLink = () => {
  const navigate = useNavigate();
  const menuDispatch = useDispatch();
  const permission = usePermission('deep-link');
  const { id: editId } = useParams();
  const isEditing = !!editId;
  const { control, handleSubmit, watch, reset, setValue, getValues } = useForm({ defaultValues: deepLinkDefaults });
  const initialEdit = useRef(null);
  const hydrating = useRef(isEditing);
  const hydratedKeys = useRef({});
  const [editRecord, setEditRecord] = useState(null);
  const [editLoading, setEditLoading] = useState(isEditing);
  const [editError, setEditError] = useState('');
  const [editWarning, setEditWarning] = useState('');
  const [unsupportedEdit, setUnsupportedEdit] = useState(false);
  const [savedEdit, setSavedEdit] = useState(false);

  const [generatedLink, setGeneratedLink] = useState('');
  const [generatedShortLink, setGeneratedShortLink] = useState('');
  const [showCopyAlert, setShowCopyAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');

  useEffect(() => {
    menuDispatch({ type: 'menuSelect', payload: 'generate-deep-link' });
  }, [menuDispatch]);
  
  // Data states
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [masterServices, setMasterServices] = useState([]);
  const [childServices, setChildServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [quickBookRoots, setQuickBookRoots] = useState([]);
  const [quickBookError, setQuickBookError] = useState('');
  
  // Selected items for title generation
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedMasterService, setSelectedMasterService] = useState(null);
  const [selectedChildService, setSelectedChildService] = useState(null);
  
  const formValues = watch();
  const keepOriginalDestination = isEditing && sameDeepLinkDestination(formValues, initialEdit.current);
  const linkType = watch('linkType');
  const categoryId = watch('categoryId');
  const subCategoryId = watch('subCategoryId');
  const masterServiceId = watch('masterServiceId');
  const quickBookMode = watch('quickBookMode');
  const quickBookPath = watch('quickBookPath') || [];
  const quickBookLevels = [];
  const selectedQuickBookNodes = [];
  let quickBookNodes = quickBookRoots;
  for (let depth = 0; quickBookMode && quickBookNodes.length && depth < 5; depth += 1) {
    quickBookLevels.push(quickBookNodes);
    const selected = quickBookNodes.find(node => node.nodeId === quickBookPath[depth]);
    if (!selected) break;
    selectedQuickBookNodes.push(selected);
    if (selected.kind === 'OFFERING') break;
    quickBookNodes = selected.children || [];
  }

  // Hydrate the complete saved form before dependent fetch effects run.
  useEffect(() => {
    if (!isEditing || !permission.canEdit || !permission.canView) return undefined;
    let active = true;
    hydrating.current = true;
    setEditLoading(true);
    setEditError('');
    DeepLinkServices.getDeepUrl(editId).then(async (response) => {
      if (Number(response?.success) !== 1 || !response.data) throw new Error(response?.message || 'Could not load this deep link.');
      if (response.data.archived_at) throw new Error('Archived links cannot be edited.');
      const loaded = await loadDeepLinkEditor(response.data, deepLinkDefaults, DeepLinkServices, QuickBookService);
      if (!active) return;
      setEditRecord(response.data);
      setUnsupportedEdit(!loaded.supported);
      setGeneratedLink(response.data.full_url);
      setGeneratedShortLink(response.data.short_url || '');
      if (loaded.supported) {
        loaded.form.expires_at = toIstInput(response.data.expires_at);
        initialEdit.current = loaded.form;
        hydratedKeys.current = { category: String(loaded.form.categoryId || ''), subCategory: String(loaded.form.subCategoryId || ''), quickBookMode: loaded.form.quickBookMode, linkType: loaded.form.linkType };
        const lists = loaded.lists;
        setCategories(lists.categories); setSubCategories(lists.subCategories);
        setMasterServices(lists.masterServices); setChildServices(lists.childServices);
        setProviders(lists.providers); setBookings(lists.bookings); setQuickBookRoots(lists.quickBookRoots);
        setSelectedCategory(lists.categories.find(row => String(row.id) === String(loaded.form.categoryId)) || null);
        setSelectedSubCategory(lists.subCategories.find(row => String(row.id) === String(loaded.form.subCategoryId)) || null);
        setSelectedMasterService(lists.masterServices.find(row => String(row.masterServiceId) === String(loaded.form.masterServiceId)) || null);
        setSelectedChildService(lists.childServices.find(row => String(row.id) === String(loaded.form.childServiceId)) || null);
        setEditWarning([response.data.edit_configuration_error, loaded.warning].filter(Boolean).join(' '));
        reset(loaded.form);
      }
      hydrating.current = false;
      setEditLoading(false);
    }).catch(error => {
      if (active) { setEditError(error.response?.data?.message || error.message || 'Could not load this deep link.'); setEditLoading(false); }
    });
    return () => { active = false; };
  }, [editId, isEditing, permission.canEdit, permission.canView, reset]);

  useEffect(() => {
    if (!isEditing && permission.canCreate) fetchCategories();
  }, [isEditing, permission.canCreate]);

  useEffect(() => {
    if (hydrating.current || editLoading) return;
    if (categoryId && ['sub-category', 'child-service'].includes(linkType)) {
      if (hydratedKeys.current.category !== String(categoryId)) fetchSubCategories(categoryId);
    } else setSubCategories([]);
  }, [categoryId, linkType, editLoading]);

  useEffect(() => {
    if (hydrating.current || editLoading) return;
    if (subCategoryId && linkType === 'child-service' && hydratedKeys.current.subCategory !== String(subCategoryId)) fetchMasterServices(subCategoryId);
  }, [subCategoryId, linkType, editLoading]);

  useEffect(() => {
    if (!hydrating.current && !editLoading && masterServiceId && linkType === 'child-service') fetchChildServices(masterServiceId);
  }, [masterServiceId, masterServices, linkType, editLoading]);

  useEffect(() => {
    if (hydrating.current || editLoading || hydratedKeys.current.linkType === linkType) return;
    if (linkType === 'provider') fetchProviders();
    else if (linkType === 'booking') fetchBookings();
  }, [linkType, editLoading]);

  const changeLinkType = (value) => {
    setValue('linkType', value);
    for (const key of ['categoryId', 'subCategoryId', 'masterServiceId', 'childServiceId', 'providerId', 'bookingId', 'quickBookMode']) setValue(key, '');
    setValue('quickBookPath', []);
    setQuickBookRoots([]); setQuickBookError(''); setGenerationError('');
    setSelectedCategory(null); setSelectedSubCategory(null); setSelectedMasterService(null); setSelectedChildService(null);
    setGeneratedLink(''); setGeneratedShortLink('');
    hydratedKeys.current = {};
    if (!categories.length) fetchCategories();
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getCategories();
      if (response.success === 1 && response.data) {
        setCategories(response.data);
      }
    } catch {
      setGenerationError('Destination options could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCategories = async (catId) => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getSubcategoriesByCategoryId(catId);
      if (String(getValues('categoryId')) !== String(catId)) return;
      if (Number(response.success) === 1 && Array.isArray(response.data)) {
        hydratedKeys.current.category = String(catId);
        setSubCategories(response.data);
      }
    } catch {
      setGenerationError('Destination options could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterServices = async (subCatId) => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getServicesBySubcategory(subCatId);
      if (String(getValues('subCategoryId')) !== String(subCatId)) return;
      if (Number(response.success) === 1 && Array.isArray(response.data)) {
        hydratedKeys.current.subCategory = String(subCatId);
        setMasterServices(response.data);
        // Child services are already included in the response, we'll extract them when master service is selected
      }
    } catch (error) {
      if (String(getValues('subCategoryId')) === String(subCatId)) setMasterServices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildServices = async (masterSvcId) => {
    // Child services are already included in the master services response
    // Extract them from the selected master service
    const selectedMaster = masterServices.find(m => m.masterServiceId == masterSvcId);
    
    if (selectedMaster && selectedMaster.childServices) {
      const childData = selectedMaster.childServices.map(child => ({
        id: child.childServiceId,
        name: child.title,
        title: child.title,
        shortTitle: child.shortTitle,
        description: child.description,
        ...child
      }));
      if (isEditing && String(initialEdit.current?.masterServiceId) === String(masterSvcId) && initialEdit.current?.childServiceId && !childData.some(child => String(child.id) === String(initialEdit.current.childServiceId))) childData.push({ id: initialEdit.current.childServiceId, title: `Saved child service #${initialEdit.current.childServiceId} (unavailable)` });
      setChildServices(childData);
    } else {
      setChildServices(isEditing && String(initialEdit.current?.masterServiceId) === String(masterSvcId) && initialEdit.current?.childServiceId ? [{ id: initialEdit.current.childServiceId, title: `Saved child service #${initialEdit.current.childServiceId} (unavailable)` }] : []);
    }
  };

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getProviders({ limit: 100 });
      if (getValues('linkType') !== 'provider') return;
      if (response.success === 1 && response.data) {
        // Handle different response structures
        const providersData = response.data.experts || 
                             response.data.providers || 
                             (Array.isArray(response.data) ? response.data : 
                             (response.data.data || response.data.list || response.data.rows || []));
        setProviders(providersData);
      }
    } catch (error) {
      if (getValues('linkType') === 'provider') setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getBookings({ limit: 100 });
      if (getValues('linkType') !== 'booking') return;
      if (response.success === 1 && response.data) {
        // Handle different response structures
        const bookingsData = response.data.bookings || 
                            (Array.isArray(response.data) ? response.data : 
                            (response.data.data || response.data.list || response.data.rows || []));
        setBookings(bookingsData);
      }
    } catch (error) {
      if (getValues('linkType') === 'booking') setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hydrating.current || editLoading || linkType !== 'quick-book' || !quickBookMode || hydratedKeys.current.quickBookMode === quickBookMode) return undefined;
    let active = true;
    setLoading(true);
    setQuickBookError('');
    setQuickBookRoots([]);
    QuickBookService.catalog(quickBookMode).then(response => {
      if (!active) return;
      if (response?.success !== 1 && response?.success !== '1') {
        throw new Error(response?.message || 'Quick Book catalog could not be loaded.');
      }
      const roots = response.data?.published?.roots || [];
      if (!roots.length) throw new Error('No published Quick Book services are available for this mode.');
      hydratedKeys.current.quickBookMode = quickBookMode;
      setQuickBookRoots(roots);
    }).catch(error => {
      if (active) setQuickBookError(error.message || 'Quick Book catalog could not be loaded.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [linkType, quickBookMode, editLoading]);

  const onSubmit = async (data) => {
    if (generating || (isEditing ? !permission.canEdit : !permission.canCreate)) return;
    setGeneratedLink('');
    setGeneratedShortLink('');
    setGenerationError('');
    // Use production URL in production, staging otherwise
    const baseUrl = isEditing ? new URL(editRecord.full_url).origin : process.env.REACT_APP_ENV_MODE === 'production'
      ? 'https://cudel.in' 
      : 'https://staging.cudel.in';
    let link = '';

    // Build UTM query string (appended before title)
    const buildUtmParams = () => {
      if (!data.addUtm) return '';
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
      return utmKeys
        .filter(k => data[k]?.trim())
        .map(k => `${k}=${encodeURIComponent(data[k].trim())}`)
        .join('&');
    };

    const appendUtmAndTitle = (baseParams, title) => {
      const utm = buildUtmParams();
      let url = baseParams;
      // if (utm) url += `&${utm}`;
      // if (title) url += `&title=${encodeURIComponent(title)}`;

      let queryParams = [];
      if (utm) queryParams.push(utm);
      if (title) queryParams.push(`title=${encodeURIComponent(title)}`);
      if (queryParams.length > 0) {
        url += (url.includes('?') ? '&' : '?') + queryParams.join('&');
      }
      return url;
    };
    
    switch (data.linkType) {
      case 'sub-category':
        if (data.categoryId && data.subCategoryId) {
          const title = selectedCategory?.name || selectedCategory?.title || selectedCategory?.categoryName || 'Category';
          link = appendUtmAndTitle(
            // `${baseUrl}/sub-category?categoryId=${data.categoryId}&subCategoryId=${data.subCategoryId}`,
            `${baseUrl}/sc/${data.categoryId}/${data.subCategoryId}`,
            title
          );
        }
        break;
      
      case 'child-service':
        if (data.masterServiceId && data.childServiceId) {
          const title = selectedMasterService?.title || selectedMasterService?.name || selectedMasterService?.serviceName || 'Service';
          link = appendUtmAndTitle(
            // `${baseUrl}/child-service?masterServiceId=${data.masterServiceId}&childServiceId=${data.childServiceId}`,
            `${baseUrl}/cs/${data.masterServiceId}/${data.childServiceId}`,
            title
          );
        }
        break;
      
      case 'provider':
        if (data.providerId) {
          // link = appendUtmAndTitle(`${baseUrl}/provider?providerId=${data.providerId}`, '');
          link = appendUtmAndTitle(`${baseUrl}/p/${data.providerId}`, '');
        }
        break;
      
      case 'booking':
        if (data.bookingId) {
          // link = appendUtmAndTitle(`${baseUrl}/booking?bookingId=${data.bookingId}`, '');
          link = appendUtmAndTitle(`${baseUrl}/b/${data.bookingId}`, '');
        }
        break;
      
      case 'quick-book': {
        const target = selectedQuickBookNodes[selectedQuickBookNodes.length - 1];
        if (target) {
          link = appendUtmAndTitle(
            `${baseUrl}/qb?mode=${data.quickBookMode === 'FIXED_SERVICE' ? 'F' : 'I'}&Id=${encodeURIComponent(target.nodeId)}`,
            target.label
          );
        }
        break;
      }

      default:
        link = '';
    }

    if (isEditing && keepOriginalDestination) link = editRecord.full_url;
    if (!link) {
      setGenerationError('Complete the link details before generating.');
      return;
    }

    setGenerating(true);
    try {
      if (isEditing) link = editDeepLinkUrl(editRecord.full_url, data, initialEdit.current, link);
      const settings = isEditing ? { full_url: link, title: data.short_title || '', expires_at: fromIstInput(data.expires_at), is_active: data.is_active } : {};
      if (data.short_title) settings.title = data.short_title;
      if (!isEditing && data.custom_alias) settings.custom_alias = data.custom_alias;
      if (data.expires_at) settings.expires_at = fromIstInput(data.expires_at);
      if (data.is_active === false) settings.is_active = false;
      const response = isEditing ? await DeepLinkServices.updateDeepUrl(editId, settings) : Object.keys(settings).length
        ? await DeepLinkServices.storeDeepUrl(link, settings)
        : await DeepLinkServices.storeDeepUrl(link);
      if (response.success === 1) {
        setGeneratedLink(response.data?.full_url || link);
        setGeneratedShortLink(response.data?.short_url || editRecord?.short_url || '');
        if (isEditing) {
          setEditRecord(previous => ({ ...previous, ...response.data, full_url: link }));
          initialEdit.current = { ...data };
          setSavedEdit(true);
        }
      } else {
        setGenerationError(response?.message || 'Could not generate the deep link. Please try again.');
      }
    } catch (error) {
      setGenerationError(error.response?.data?.message || 'Could not generate the deep link. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = async (url = generatedLink) => {
    try { await navigator.clipboard.writeText(url); setShowCopyAlert(true); }
    catch { setGenerationError('Could not copy the link. Select the URL and copy it manually.'); }
  };

  const handleReset = () => {
    setSavedEdit(false);
    reset(isEditing ? initialEdit.current : deepLinkDefaults);
    setGeneratedLink(isEditing ? editRecord.full_url : '');
    setGeneratedShortLink(isEditing ? editRecord.short_url || '' : '');
    setGenerationError('');
  };

  if (isEditing ? (!permission.canEdit || !permission.canView) : !permission.canCreate) return <Alert severity="error">You do not have permission to {isEditing ? 'edit' : 'generate'} deep links.</Alert>;
  if (editLoading) return <Box sx={{ p: 3 }}><CircularProgress aria-label="Loading deep link" /></Box>;
  if (editError) return <Alert severity="error">{editError}</Alert>;
  if (unsupportedEdit) return <><Alert severity="info">This destination does not use a supported generator format. Its original URL is preserved in the URL editor.</Alert><ShortLinkDialog link={editRecord} mode="edit" onClose={() => navigate('/marketing/generate-deep-link')} onSaved={() => navigate('/marketing/generate-deep-link')} /></>;

  return (
    <>
      <PageHeaderLayout headerTitle={isEditing ? 'Edit Deep Link' : 'Generate Deep Link'}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', minWidth: 0 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/marketing/generate-deep-link')}
        >
          Show List
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleReset}
          sx={{ fontSize: { xs: '.5rem', sm: '.75rem' } }}
        >
          {isEditing ? 'Reset Changes' : 'Reset Form'}
        </Button>
        </Box>
      </PageHeaderLayout>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {editWarning && <Alert severity="warning" sx={{ mb: 2 }}>{editWarning}</Alert>}
        {isEditing && <Alert severity="info" sx={{ mb: 2 }}>Edit the saved destination and campaign settings below. The short alias stays fixed.</Alert>}
        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Deep Link Configuration
                </Typography>
                
                <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
                  <Controller
                    name="linkType"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Link Type"
                        onChange={(event) => changeLinkType(event.target.value)}
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        <MenuItem value="sub-category">Sub-Category</MenuItem>
                        <MenuItem value="child-service">Child Service</MenuItem>
                        <MenuItem value="provider">Service Expert</MenuItem>
                        <MenuItem value="booking">Booking</MenuItem>
                        <MenuItem value="quick-book">Quick Book</MenuItem>
                       
                      </TextField>
                    )}
                  />

                  {/* Sub-Category Link Fields */}
                  {linkType === 'sub-category' && (
                    <>
                      <Controller
                        name="categoryId"
                        control={control}
                        rules={{ required: keepOriginalDestination ? false : 'Category is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={categories}
                            getOptionLabel={(option) => option.name || option.title || option.categoryName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                            loading={loading}
                            onChange={(_, value) => {
                              field.onChange(value?.id || '');
                              setSelectedCategory(value);
                              setValue('subCategoryId', ''); setValue('masterServiceId', ''); setValue('childServiceId', '');
                              setSelectedSubCategory(null); setSelectedMasterService(null); setSelectedChildService(null);
                              setMasterServices([]); setChildServices([]);
                            }}
                            value={categories.find(c => c.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Category"
                                error={!!error}
                                helperText={error?.message}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />

                      <Controller
                        name="subCategoryId"
                        control={control}
                        rules={{ required: keepOriginalDestination ? false : 'Sub-Category is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={subCategories}
                            getOptionLabel={(option) => option.name || option.title || option.subCategoryName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                            loading={loading}
                            disabled={!categoryId}
                            onChange={(_, value) => {
                              field.onChange(value?.id || '');
                              setSelectedSubCategory(value);
                              setValue('masterServiceId', ''); setValue('childServiceId', '');
                              setSelectedMasterService(null); setSelectedChildService(null); setChildServices([]);
                            }}
                            value={subCategories.find(s => s.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Sub-Category"
                                error={!!error}
                                helperText={error?.message || 'Select category first'}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                    </>
                  )}

                  {/* Child Service Link Fields */}
                  {linkType === 'child-service' && (
                    <>
                      <Controller
                        name="categoryId"
                        control={control}
                        rules={{ required: keepOriginalDestination ? false : 'Category is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={categories}
                            getOptionLabel={(option) => option.name || option.title || option.categoryName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                            loading={loading}
                            onChange={(_, value) => {
                              field.onChange(value?.id || '');
                              setSelectedCategory(value);
                              setValue('subCategoryId', ''); setValue('masterServiceId', ''); setValue('childServiceId', '');
                              setSelectedSubCategory(null); setSelectedMasterService(null); setSelectedChildService(null);
                              setMasterServices([]); setChildServices([]);
                            }}
                            value={categories.find(c => c.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Category"
                                error={!!error}
                                helperText={error?.message}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />

                      <Controller
                        name="subCategoryId"
                        control={control}
                        rules={{ required: keepOriginalDestination ? false : 'Sub-Category is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={subCategories}
                            getOptionLabel={(option) => option.name || option.title || option.subCategoryName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                            loading={loading}
                            disabled={!categoryId}
                            onChange={(_, value) => {
                              field.onChange(value?.id || '');
                              setSelectedSubCategory(value);
                              setValue('masterServiceId', ''); setValue('childServiceId', '');
                              setSelectedMasterService(null); setSelectedChildService(null); setChildServices([]);
                            }}
                            value={subCategories.find(s => s.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Sub-Category"
                                error={!!error}
                                helperText={error?.message || 'Select category first'}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />

                      <Controller
                        name="masterServiceId"
                        control={control}
                        rules={{ required: keepOriginalDestination ? false : 'Master Service is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            options={masterServices}
                            getOptionLabel={(option) => option.title || option.name || option.serviceName || `ID: ${option.masterServiceId}`}
                            isOptionEqualToValue={(option, value) => {
                              const result = option.masterServiceId == value?.masterServiceId || option.masterServiceId == value;
                              return result;
                            }}
                            loading={loading}
                            disabled={!subCategoryId}
                            onChange={(_, value) => {
                              const newId = value?.masterServiceId || '';
                              field.onChange(newId);
                              setSelectedMasterService(value);
                              setValue('childServiceId', ''); setSelectedChildService(null);
                            }}
                            value={masterServices.find(m => m.masterServiceId == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Master Service"
                                error={!!error}
                                helperText={error?.message || `Select sub-category first. Current value: ${field.value || 'none'}`}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />

                      <Controller
                        name="childServiceId"
                        control={control}
                        rules={{ required: keepOriginalDestination ? false : 'Child Service is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            options={childServices}
                            getOptionLabel={(option) => option.title || option.name || option.serviceName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => {
                              const result = option.id == value?.id || option.id == value;
                              return result;
                            }}
                            loading={loading}
                            disabled={!masterServiceId}
                            noOptionsText={masterServiceId ? "No child services available" : "Select master service first"}
                            onChange={(_, value) => {
                              const newId = value?.id || '';
                              field.onChange(newId);
                              setSelectedChildService(value);
                            }}
                            value={childServices.find(c => c.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Child Service"
                                error={!!error}
                                helperText={error?.message || `Select master service first. Options: ${childServices.length}. Current value: ${field.value || 'none'}`}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                    </>
                  )}

                  {/* Provider Link Fields */}
                  {linkType === 'provider' && (
                    <Controller
                      name="providerId"
                      control={control}
                      rules={{ required: keepOriginalDestination ? false : 'Service Expert is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={providers}
                          getOptionLabel={(option) => {
                            const name = option.title || option.name || option.fullName || option.firstName || option.UserProfile?.firstName || 'Unknown';
                            const mobile = option.mobile || option.phone || option.mobileNumber || option.mobileNo || '';
                            const id = option.id || '';
                            return `${name}${mobile ? ' - ' + mobile : ''} (ID: ${id})`;
                          }}
                          isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;
                            const searchTerm = inputValue.toLowerCase();
                            return options.filter(option => {
                              const name = (option.name || option.fullName || option.firstName || option.UserProfile?.firstName || '').toLowerCase();
                              const mobile = (option.mobile || option.phone || option.mobileNumber || option.mobileNo || '').toLowerCase();
                              const id = String(option.id || '');
                              return name.includes(searchTerm) || mobile.includes(searchTerm) || id.includes(searchTerm);
                            });
                          }}
                          loading={loading}
                          onChange={(_, value) => {
                            field.onChange(value?.id || '');
                          }}
                          value={providers.find(p => p.id == field.value) || null}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Service Expert (Search by Name)"
                              error={!!error}
                              helperText={error?.message || 'Search by expert name, mobile or ID'}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ mb: 2 }}
                        />
                      )}
                    />
                  )}

                  {/* Booking Link Fields */}
                  {linkType === 'booking' && (
                    <Controller
                      name="bookingId"
                      control={control}
                      rules={{ required: keepOriginalDestination ? false : 'Booking ID is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          freeSolo
                          options={bookings}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return `Booking #${option.id} - ${option.customerName || 'N/A'}`;
                          }}
                          loading={loading}
                          onChange={(_, value) => {
                            if (typeof value === 'string') {
                              field.onChange(value);
                            } else {
                              field.onChange(value?.id || '');
                            }
                          }}
                          onInputChange={(_, newValue) => {
                            if (newValue && !isNaN(newValue)) {
                              field.onChange(newValue);
                            }
                          }}
                          value={bookings.find(b => b.id == field.value) || field.value || null}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Booking ID"
                              error={!!error}
                              helperText={error?.message || 'Select from dropdown or enter booking ID manually'}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ mb: 2 }}
                        />
                      )}
                    />
                  )}

                  {linkType === 'quick-book' && (
                    <>
                      <Controller
                        name="quickBookMode"
                        control={control}
                        rules={{ required: keepOriginalDestination ? false : 'Quick Book mode is required' }}
                        render={({ field, fieldState: { error } }) => {
                          const changeMode = (value) => {
                            field.onChange(value);
                            hydratedKeys.current.quickBookMode = null;
                            setValue('quickBookPath', []);
                            setQuickBookRoots([]);
                            setQuickBookError('');
                            setGeneratedLink('');
    setGeneratedShortLink('');
                            setGenerationError('');
                          };
                          return (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              label="Quick Book mode"
                              error={!!error}
                              helperText={error?.message}
                              onChange={(event) => changeMode(event.target.value)}
                              InputProps={{
                                endAdornment: field.value && (
                                  <InputAdornment position="end" sx={{ mr: 4 }}>
                                    <IconButton
                                      size="small"
                                      aria-label="Clear Quick Book mode"
                                      onMouseDown={(event) => event.stopPropagation()}
                                      onClick={(event) => { event.stopPropagation(); changeMode(''); }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ mb: 2 }}
                            >
                              <MenuItem value="FIXED_SERVICE">{quickBookModeLabel('FIXED_SERVICE')}</MenuItem>
                              <MenuItem value="INSPECTION_FIRST">{quickBookModeLabel('INSPECTION_FIRST')}</MenuItem>
                            </TextField>
                          );
                        }}
                      />
                      {isEditing && keepOriginalDestination && initialEdit.current?.quickBookTarget && <TextField label="Saved Quick Book destination" value={initialEdit.current.quickBookTarget} fullWidth InputProps={{ readOnly: true }} helperText="The saved target is preserved unless you select a replacement below." sx={{ mb: 2 }} />}
                      {quickBookError && <Alert severity="error" sx={{ mb: 2 }}>{quickBookError}</Alert>}
                      {quickBookLevels.map((nodes, depth) => (
                        <Controller
                          key={`${quickBookMode}-${depth}`}
                          name={`quickBookPath.${depth}`}
                          control={control}
                          render={({ field, fieldState: { error } }) => {
                            const label = nodes.every(node => node.kind === 'OFFERING')
                              ? 'Quick Book service'
                              : ({ CATEGORY: 'Quick Book category', SUBCATEGORY: 'Quick Book sub-category', MASTER_SERVICE: 'Quick Book master service', CHILD_SERVICE: 'Quick Book child service', BRAND: 'Quick Book brand' })[nodes[0]?.source?.type]
                                || `Quick Book step ${depth + 1}`;
                            const changeSelection = (value) => {
                              setValue('quickBookPath', [
                                ...quickBookPath.slice(0, depth),
                                ...(value ? [value] : []),
                              ]);
                              setGeneratedLink('');
    setGeneratedShortLink('');
                              setGenerationError('');
                            };
                            return (
                              <TextField
                                {...field}
                                value={field.value || ''}
                                select
                                fullWidth
                                label={label}
                                error={!!error}
                                helperText={error?.message}
                                onChange={(event) => changeSelection(event.target.value)}
                                InputProps={{
                                  endAdornment: field.value && (
                                    <InputAdornment position="end" sx={{ mr: 4 }}>
                                      <IconButton
                                        size="small"
                                        aria-label={`Clear ${label}`}
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={(event) => { event.stopPropagation(); changeSelection(''); }}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{ mb: 2 }}
                              >
                                  {nodes.map(node => (
                                  <MenuItem key={node.nodeId} value={node.nodeId}>{node.label}</MenuItem>
                                ))}
                              </TextField>
                            );
                          }}
                        />
                      ))}
                    </>
                  )}

                  {/* UTM Parameters */}
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Controller
                      name="addUtm"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Add UTM Parameters"
                        />
                      )}
                    />
                  </Box>
                  <Collapse in={!!watch('addUtm')}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {[
                        { name: 'utm_source', label: 'UTM Source', placeholder: 'e.g. whatsapp' },
                        { name: 'utm_medium', label: 'UTM Medium', placeholder: 'e.g. notification' },
                        { name: 'utm_campaign', label: 'UTM Campaign', placeholder: 'e.g. summer_sale' },
                        { name: 'utm_term', label: 'UTM Term', placeholder: 'e.g. ac_repair' },
                        { name: 'utm_content', label: 'UTM Content', placeholder: 'e.g. banner_1' },
                      ].map(({ name, label, placeholder }) => (
                        <Grid item xs={12} sm={6} key={name}>
                          <Controller
                            name={name}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={label}
                                fullWidth
                                size="small"
                                placeholder={placeholder}
                              />
                            )}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Collapse>

                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 2 }}>Short Link Settings</Typography>
                  <Controller name="short_title" control={control} rules={{ maxLength: { value: 200, message: 'Use at most 200 characters.' } }} render={({ field, fieldState }) => <TextField {...field} label="Link title (optional)" fullWidth size="small" error={!!fieldState.error} helperText={fieldState.error?.message} sx={{ mb: 2 }} />} />
                  <Controller name="custom_alias" control={control} render={({ field, fieldState }) => <TextField {...field} label={isEditing ? 'Short alias (fixed)' : 'Custom alias (optional)'} disabled={isEditing} fullWidth size="small" error={!!fieldState.error} helperText={fieldState.error?.message || 'Leave blank for a random alias. Spaces and special characters are encoded in the short URL. Aliases cannot be changed or reused.'} sx={{ mb: 2 }} />} />
                  <Controller name="expires_at" control={control} render={({ field }) => <TextField {...field} label="Expiry (IST)" type="datetime-local" fullWidth size="small" InputLabelProps={{ shrink: true }} helperText="Leave blank for no expiry. Time is in India Standard Time." sx={{ mb: 1 }} />} />
                  <Controller name="is_active" control={control} render={({ field }) => <FormControlLabel control={<Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />} label="Active" />} />
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={generating || (linkType === 'quick-book' && !selectedQuickBookNodes.length && !keepOriginalDestination)}
                    startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                    sx={{ mt: 2 }}
                  >
                    {generating ? (isEditing ? 'Saving…' : 'Generating…') : (isEditing ? 'Save Changes' : 'Generate Deep Link')}
                  </Button>
                  {generationError && <Alert severity="error" sx={{ mt: 2 }}>{generationError}</Alert>}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Result Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Generated Deep Link
                </Typography>
                
                {generatedLink ? (
                  <Box sx={{ mt: 2 }}>
                    {generatedShortLink && <TextField
                      label="Short URL"
                      value={generatedShortLink}
                      fullWidth
                      InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><IconButton aria-label="Copy short URL" onClick={() => handleCopyLink(generatedShortLink)} color="primary"><ContentCopyIcon /></IconButton></InputAdornment> }}
                      sx={{ mb: 2 }}
                    />}
                    <TextField
                      label="Full deep link URL"
                      value={generatedLink}
                      fullWidth
                      multiline
                      rows={3}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleCopyLink(generatedLink)}
                              aria-label="Copy full URL"
                              edge="end"
                              color="primary"
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {isEditing ? (savedEdit ? 'Deep link updated successfully. Its short URL is unchanged.' : 'Saved deep link loaded. Edit the fields and save your changes.') : 'Deep link generated successfully! Click the copy icon to copy the link.'}
                    </Alert>

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Usage Instructions:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Use this link in notifications, emails, or SMS
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • App opening depends on the browser, device and installed-app configuration
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Ensure the app is installed on the user's device
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    mt: 2, 
                    p: 4, 
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}>
                    <LinkIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Fill in the form and click "Generate Deep Link" to create your link
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={showCopyAlert}
        autoHideDuration={2000}
        onClose={() => setShowCopyAlert(false)}
        message="Link copied to clipboard!"
      />
    </>
  );
};

export default GenerateDeepLink;
